import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { meApi } from '../../api/endpoints/me';
import { handleApiError } from '../../api/errorMapping';
import { ApiError, SessionExpiredError } from '../../api/errors';
import { useAuthStore } from '../../stores/authStore';
import { SecurityPageSection } from '../layouts/SecurityPageSection';
import { Banner } from '../ui/Banner';
import { groupSecret } from '../../lib/totp';
import { Button, CopyableId, Input, Loading } from '@/kit';

/**
 * Self-service: usuario autenticado configura una app autenticadora (EMAIL → TOTP).
 * Flujo: enroll-totp (al montar) → QR + clave en texto + recovery codes → verificar el código.
 * NO comparte estado con el wizard de primer-login (MfaTotpWizard).
 */

type Stage = 'INIT' | 'SHOW_QR' | 'ERROR';

type VerifyError = 'INVALID_CODE' | 'GENERIC';

interface EnrollData {
    secret: string;
    qrDataUrl: string;
    recoveryCodes: string[];
    enrollmentToken: string;
}

/** Código incorrecto o token de enrolamiento vencido: el backend no los distingue (ambos 4xx). */
function isRejectedCode(err: unknown): boolean {
    if (!(err instanceof ApiError)) return false;
    if (err.code === 'INVALID_CREDENTIALS') return true;
    return err.code === 'TOTP_SECURITY_ERROR' && err.httpStatus < 500;
}

export function MfaTotpSetup() {
    const { t } = useTranslation(['auth']);
    const qc = useQueryClient();
    const navigate = useNavigate();
    const refreshProfile = useAuthStore((s) => s.refresh);
    const clearSession = useAuthStore((s) => s.clear);

    const [stage, setStage] = useState<Stage>('INIT');
    const [enroll, setEnroll] = useState<EnrollData | null>(null);
    const [totpCode, setTotpCode] = useState('');
    const [verifyError, setVerifyError] = useState<VerifyError | null>(null);
    // Guard contra el doble-invoke de StrictMode (mismo patrón que MfaEmailSwitch.sentRef):
    // el enroll-totp DEBE dispararse una sola vez por entrada al stage INIT. El botón
    // "Reintentar" lo rearma explícitamente (ver onClick) para permitir un reintento real.
    const sentRef = useRef(false);

    const enrollMut = useMutation({
        mutationFn: () => meApi.enrollTotp(),
        onSuccess: (resp) => {
            setEnroll({
                secret: resp.secret,
                qrDataUrl: resp.qrDataUrl,
                recoveryCodes: resp.recoveryCodes,
                enrollmentToken: resp.enrollmentToken,
            });
            setStage('SHOW_QR');
        },
        onError: (err) => {
            // Sin esto el stage queda en 'INIT' → spinner infinito.
            handleApiError(err);
            setStage('ERROR');
        },
    });

    const verifyMut = useMutation({
        mutationFn: () => {
            if (!enroll) throw new Error('enrollmentToken requerido para verificar');
            return meApi.verifyTotpEnrollment({
                totpCode,
                enrollmentToken: enroll.enrollmentToken,
            });
        },
        onSuccess: async () => {
            setVerifyError(null);
            toast.success('Aplicación de autenticación configurada');
            // Relee el perfil para que el guard no use el mfaSetupRequired viejo de la caché.
            await refreshProfile();
            await qc.invalidateQueries({ queryKey: ['me', 'security', 'mfa'] });
            void navigate({ to: '/me/profile' });
        },
        onError: (err) => {
            if (isRejectedCode(err)) {
                setTotpCode('');
                setVerifyError('INVALID_CODE');
                return;
            }
            // El refresh ya falló en apiFetch: la sesión murió (este onError reemplaza al global).
            if (err instanceof SessionExpiredError) {
                clearSession();
                void navigate({ to: '/login' });
                return;
            }
            setVerifyError('GENERIC');
        },
    });

    // Enrolamiento nuevo: otro QR, otra clave y otro token (el anterior pudo vencer).
    const restartEnrollment = (): void => {
        sentRef.current = false;
        setEnroll(null);
        setTotpCode('');
        setVerifyError(null);
        setStage('INIT');
    };

    useEffect(() => {
        if (stage !== 'INIT') return;
        if (sentRef.current) return;
        sentRef.current = true;
        enrollMut.mutate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage]);

    if (stage === 'ERROR')
        return (
            <SecurityPageSection title="Aplicación de autenticación" centered>
                <div className="max-w-md space-y-4">
                    <p className="text-sm text-muted-foreground">
                        No pudimos iniciar la configuración del autenticador. Vuelve a intentarlo en
                        unos segundos.
                    </p>
                    <div className="flex gap-3">
                        <Button
                            variant="outline"
                            onClick={() => {
                                void navigate({ to: '/me/profile' });
                            }}
                        >
                            Volver
                        </Button>
                        <Button onClick={restartEnrollment}>Reintentar</Button>
                    </div>
                </div>
            </SecurityPageSection>
        );

    // El gate del spinner es el STAGE + enroll (que onSuccess setea de forma confiable), NO
    // enrollMut.isPending: bajo StrictMode el observer de la mutation se desincroniza en el
    // doble-mount y deja isPending pegado en true aunque onSuccess ya corrió → spinner eterno.
    if (stage === 'INIT' || !enroll)
        return (
            <SecurityPageSection title="Aplicación de autenticación" centered>
                <Loading text="Generando código…" />
            </SecurityPageSection>
        );

    return (
        <SecurityPageSection title="Aplicación de autenticación" centered>
            <p className="max-w-md text-sm text-muted-foreground">
                Escanea este código QR con tu app (Google Authenticator, Authy, etc.).
            </p>
            <div className="max-w-md space-y-4">
                <div className="flex justify-center">
                    <img
                        src={enroll.qrDataUrl}
                        alt="Código QR para la app de autenticación"
                        width={200}
                        height={200}
                        className="rounded-md border"
                    />
                </div>

                <div className="space-y-2 rounded-lg border p-4">
                    <p className="text-sm font-medium">{t('auth:mfa.totpSetup.manualTitle')}</p>
                    <p className="text-xs text-muted-foreground">
                        {t('auth:mfa.totpSetup.manualHint')}
                    </p>
                    <CopyableId
                        label={t('auth:mfa.totpSetup.secretLabel')}
                        value={enroll.secret}
                        display={groupSecret(enroll.secret)}
                        wrap
                    />
                </div>

                <div className="space-y-2 rounded-lg border border-warning/40 bg-warning/10 p-4">
                    <p className="text-sm font-medium text-warning">Códigos de recuperación</p>
                    <p className="text-xs text-warning">
                        Guarda estos códigos en un lugar seguro. Se muestran una sola vez y te
                        permiten recuperar el acceso si pierdes tu app.
                    </p>
                    <ul className="grid grid-cols-2 gap-1 font-mono text-sm text-warning">
                        {enroll.recoveryCodes.map((rc) => (
                            <li key={rc}>{rc}</li>
                        ))}
                    </ul>
                </div>

                <Input
                    id="totp-setup-code"
                    label="Ingresa el código de 6 dígitos de tu app"
                    inputMode="numeric"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => {
                        setTotpCode(e.target.value);
                        setVerifyError(null);
                    }}
                />
                {verifyError && (
                    <Banner variant="error">
                        <div className="space-y-2">
                            <p>
                                {verifyError === 'INVALID_CODE'
                                    ? t('auth:mfa.totpSetup.invalidCode')
                                    : t('auth:mfa.totpSetup.genericError')}
                            </p>
                            {verifyError === 'INVALID_CODE' && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={restartEnrollment}
                                >
                                    {t('auth:mfa.totpSetup.regenerate')}
                                </Button>
                            )}
                        </div>
                    </Banner>
                )}
                <Button
                    className="w-full"
                    onClick={() => {
                        verifyMut.mutate();
                    }}
                    disabled={totpCode.length !== 6 || verifyMut.isPending}
                >
                    Verificar y activar
                </Button>
            </div>
        </SecurityPageSection>
    );
}
